"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TypedObjectFrameRole = void 0;
exports.findCrossKindFrameConflicts = findCrossKindFrameConflicts;
exports.readTypedObjectFrames = readTypedObjectFrames;
const OrcadDsnCfbStreamUtils_1 = require("./OrcadDsnCfbStreamUtils");
var TypedObjectFrameRole;
(function (TypedObjectFrameRole) {
    TypedObjectFrameRole["Owner"] = "owner";
    TypedObjectFrameRole["Pin"] = "pin";
    TypedObjectFrameRole["Display"] = "display";
})(TypedObjectFrameRole || (exports.TypedObjectFrameRole = TypedObjectFrameRole = {}));
function findCrossKindFrameConflicts(groups, diagnostics, streamPath = '') {
    const claims = groups
        .flatMap(({ frames, role }, group) => [...frames.values()].map((frame) => ({ frame, group, role })))
        .toSorted((left, right) => left.frame.offset - right.frame.offset);
    const conflicts = new Set();
    let active = [];
    for (const claim of claims) {
        active = active.filter((previous) => previous.frame.endOffset > claim.frame.offset);
        for (const previous of active) {
            const acceptsChild = (previous.role === TypedObjectFrameRole.Owner &&
                (claim.role === TypedObjectFrameRole.Pin || claim.role === TypedObjectFrameRole.Display)) ||
                (previous.role === TypedObjectFrameRole.Pin && claim.role === TypedObjectFrameRole.Display);
            if (previous.group === claim.group ||
                (acceptsChild &&
                    claim.frame.offset >= previous.frame.dataOffset &&
                    claim.frame.endOffset <= previous.frame.endOffset)) {
                continue;
            }
            conflicts.add(previous.frame.dataOffset);
            conflicts.add(claim.frame.dataOffset);
            diagnostics.push({
                severity: 'warning',
                code: 'typed-frame-cross-kind-overlap',
                streamPath,
                offset: claim.frame.dataOffset,
                message: `CFB typed frames [${previous.frame.offset},${previous.frame.endOffset}) and [${claim.frame.offset},${claim.frame.endOffset}) overlap without a complete owner-child boundary; both withheld`,
            });
        }
        active.push(claim);
    }
    return conflicts;
}
function readTypedObjectFrames(bytes, kinds, baseDepth, diagnostics = [], streamPath = '') {
    const frames = new Map();
    const conflicts = new Set();
    const baseHeaders = new Set();
    const tails = new Map();
    const headerEnd = (offset) => {
        if (offset + 9 > bytes.length ||
            !kinds.includes(bytes[offset]) ||
            OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 5) !== 0) {
            return undefined;
        }
        const endOffset = offset + 9 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 1);
        return endOffset > offset + 9 && endOffset <= bytes.length ? endOffset : undefined;
    };
    const readTail = (offset) => {
        const path = [];
        let current = offset;
        let tail;
        while (true) {
            if (tails.has(current)) {
                tail = tails.get(current);
                break;
            }
            const currentEnd = headerEnd(current);
            if (currentEnd === undefined) {
                tails.set(current, undefined);
                break;
            }
            const cursor = current + 9;
            const nestedEnd = headerEnd(cursor);
            if (nestedEnd !== undefined && nestedEnd <= currentEnd) {
                path.push({ headerEndOffset: currentEnd, offset: current });
                current = cursor;
                continue;
            }
            if (cursor + 3 > currentEnd || bytes[cursor] !== bytes[current]) {
                tails.set(current, undefined);
                break;
            }
            const storedCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, cursor + 1);
            const count = storedCount === 0xffff ? 0 : storedCount;
            const terminator = cursor + 3 + count * 8;
            if (terminator + 4 > currentEnd ||
                !OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.hasBytes(bytes, terminator, [0xff, 0xe4, 0x5c, 0x39])) {
                tails.set(current, undefined);
                break;
            }
            const remaining = currentEnd - terminator;
            const hasContinuation = remaining > 8 && terminator + 8 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, terminator + 4) === currentEnd;
            if (remaining !== 4 && remaining !== 8 && !hasContinuation) {
                tails.set(current, undefined);
                break;
            }
            tail = {
                dataOffset: terminator + (remaining === 8 ? 8 : 4),
                depth: 1,
                headerEndOffset: currentEnd,
                ...(remaining === 8 && !OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.hasBytes(bytes, terminator + 4, [0, 0, 0, 0]) ?
                    { reservedTerminatorOffset: terminator }
                    : {}),
            };
            tails.set(current, tail);
            break;
        }
        while (path.length > 0) {
            const parent = path.pop();
            if (parent === undefined || tail === undefined || tail.headerEndOffset > parent.headerEndOffset) {
                tail = undefined;
                if (parent !== undefined) {
                    tails.set(parent.offset, undefined);
                }
                continue;
            }
            tail = {
                dataOffset: tail.dataOffset,
                depth: tail.depth + 1,
                headerEndOffset: parent.headerEndOffset,
                nextHeaderOffset: current,
                ...(tail.reservedTerminatorOffset === undefined ?
                    {}
                    : { reservedTerminatorOffset: tail.reservedTerminatorOffset }),
            };
            tails.set(parent.offset, tail);
            current = parent.offset;
        }
        return tails.get(offset);
    };
    for (let offset = 0; offset + 9 <= bytes.length; offset++) {
        if (baseHeaders.has(offset)) {
            continue;
        }
        const endOffset = headerEnd(offset);
        if (endOffset === undefined) {
            continue;
        }
        const tail = readTail(offset + 9);
        if (tail === undefined ||
            tail.headerEndOffset > endOffset ||
            (baseDepth !== undefined && tail.depth !== baseDepth) ||
            tail.dataOffset >= endOffset) {
            continue;
        }
        if (tail.reservedTerminatorOffset !== undefined) {
            diagnostics.push({
                severity: 'warning',
                code: 'typed-frame-terminator-unverified',
                streamPath,
                offset: tail.reservedTerminatorOffset,
                message: `CFB typed frame at ${offset}: declared eight-byte terminator has nonzero reserved bytes; frame withheld`,
            });
            continue;
        }
        for (let header = offset + 9;;) {
            baseHeaders.add(header);
            const nestedTail = tails.get(header);
            if (nestedTail?.nextHeaderOffset === undefined) {
                break;
            }
            header = nestedTail.nextHeaderOffset;
        }
        const frame = { offset, dataOffset: tail.dataOffset, endOffset };
        if (frames.has(frame.dataOffset)) {
            conflicts.add(frame.dataOffset);
            diagnostics.push({
                severity: 'warning',
                code: 'typed-frame-data-offset-collision',
                streamPath,
                offset: frame.dataOffset,
                message: `CFB typed frames at ${frames.get(frame.dataOffset)?.offset} and ${frame.offset} claim one data offset; both withheld`,
            });
        }
        else {
            frames.set(frame.dataOffset, frame);
        }
    }
    for (const offset of conflicts) {
        frames.delete(offset);
    }
    return frames;
}
//# sourceMappingURL=OrcadDsnCfbTypedObjectFrames.js.map